import turtleIllustration from '@/assets/turtle.svg';
import {
  AccessibleDivButton,
  Divider,
  EmptyState,
  FlyerCard,
  HeaderFixed,
  LoadingSpinner,
  NoActiveFlyersFound,
  NoSearchResultsFoundEmptyState,
  SortMenu,
} from '@/components';
import { MasonryBreakpointCols } from '@/config/constants';
import FlyersContext from '@/context/FlyersContext';
import { flyerboardActiveFiltersVar } from '@/graphql/state';
import { AllFlyerFilters, FlyerCardThumbnail } from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useReactiveVar } from '@apollo/client';
import { BodyLargeEmphasized, ButtonFlatSmall, SearchBox, Tag } from 'design-system';

import {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry from 'react-masonry-css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './ExploreResults.styles';

import UserPreferencesContext from '@/context/UserPreferencesContext';
import { districtVar } from '@/graphql/state';
import { useFlyerboardPagination, useNavigationPreviousPage, useSchools } from '@/hooks';
import colors from '@/theme/colors';
import { FlyerStatus } from '@/types/flyer';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { NavigationContext } from '@/context/NavigationContext';

export const ExploreResults: React.FC = () => {
  const { search } = useParams();
  const { activeTab, targetTab } = useContext(FlyersContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const audienceId = searchParams.get('audienceId') || 'all';
  const district = useReactiveVar(districtVar);
  const [searchInputValue, setSearchQuery] = useState(search || '');
  const [searchQuery, setSearchTerm] = useState(search || '');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { hasMultipleChildren } = useSchools();
  const [selectedSortValue, setSelectedSortValue] = useState<string>(
    searchParams.get('sort') || 'flyerId'
  );
  const trackId = 'explore_results_page';
  const { community, viewWholeCommunityFlyers } = useContext(UserPreferencesContext);
  const { wasFlyerDetailsPage, isLoading: isUpdatingNav } = useContext(NavigationContext);
  const trackingEventDetail = EventDetails.search('explore');
  const navigatePreviousPage = useNavigationPreviousPage();
  const { lat, lng } = community?.location;

  const sortOptions = [
    { value: 'flyerId', label: 'Recently posted' },
    { value: 'owningType', label: 'Provider', description: 'School, Community' },
    ...(hasMultipleChildren ? [{ value: 'gradeOrAge', label: 'Grade, Age' }] : []),
  ];

  const getTracking = useCallback(
    () => ({
      event: TrackingEvents.search,
      event_detail: trackingEventDetail,
      stage: TrackingStage.discovery,
      search_term: searchInputValue,
    }),
    [trackingEventDetail, searchInputValue]
  );

  useEffect(() => {
    if (!isUpdatingNav) {
      trackEvent({
        ...getTracking(),
        ...(wasFlyerDetailsPage
          ? {
              event_from: TrackingEvents.viewFlyerDetails,
              event_detail_from: TrackingEvents.viewFlyerDetails,
              stage_from: TrackingStage.consideration,
            }
          : {}),
      });
    }
  }, [isUpdatingNav, wasFlyerDetailsPage]);

  const activeFlyersFilters: AllFlyerFilters = useMemo(
    () => ({
      searchQuery,
      schoolIds: [],
      allDistrictSchools: !!viewWholeCommunityFlyers,
      isInMySchool: true,
      communityLocation: community?.location ? { lat, lng } : undefined,
      communityRadius: community.distance ? community.distance : undefined,
    }),
    [searchQuery]
  );

  const {
    flyers: activeFlyers,
    loading: loadingActiveFlyers,
    fetchMoreFlyers: fetchMoreActiveFlyers,
    total: activeFlyersTotal,
    isPaginationEnd,
  } = useFlyerboardPagination({
    flyerType: activeTab,
    audienceId,
    districtId: district?.districtId,
    flyerboardFilters: activeFlyersFilters,
    sortBy: selectedSortValue,
    skip: !district,
  });

  const expiredFlyersFilters = useMemo<AllFlyerFilters>(
    () => ({
      ...activeFlyersFilters,
      isExpired: true,
    }),
    [activeFlyersFilters]
  );

  const {
    flyers: expiredFlyers,
    loading: loadingExpiredFlyers,
    fetchMoreFlyers: fetchMoreExpiredFlyers,
    total: expiredFlyersTotal,
  } = useFlyerboardPagination({
    skip: !isPaginationEnd,
    flyerType: activeTab,
    audienceId,
    districtId: district?.districtId,
    flyerboardFilters: expiredFlyersFilters,
    shouldUpdateFacets: false,
  });

  const loading = loadingActiveFlyers || loadingExpiredFlyers;

  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);

  const isFiltering = useMemo(
    () => flyerboardActiveFilters.grades.length > 0 || flyerboardActiveFilters.types.length > 0,
    [flyerboardActiveFilters]
  );

  const clearActiveFilters = () => {
    flyerboardActiveFiltersVar({
      grades: [],
      types: [],
    });
  };

  const onSearchInputKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && searchInputValue.length > 0) {
      setSearchTerm(searchInputValue);
      clearActiveFilters();
      const encodedSearch = encodeURIComponent(searchInputValue);

      navigate(`/explore/${encodedSearch}/results`, {
        replace: true,
        state: {
          event_from: TrackingEvents.search,
          event_detail_from: trackingEventDetail,
          stage_from: TrackingStage.discovery,
        },
      });
    }
  };

  const handleSortChange = (value: string) => {
    setSelectedSortValue(value);
    setSearchParams({ sort: value });
    trackEvent({
      event: TrackingEvents.sortExploreFlyers,
      event_detail: TrackingEvents.sortExploreFlyers,
      stage: TrackingStage.consideration,
      event_from: TrackingEvents.search,
      event_detail_from: trackingEventDetail,
      stage_from: TrackingStage.discovery,
      search_term: searchInputValue,
    });

    handleCloseDrawer();
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    anchorRef.current?.focus();
  };

  const renderInfiniteScroll = ({
    flyers,
    total,
    nextPage,
  }: {
    flyers: FlyerCardThumbnail[];
    total: number;
    nextPage: () => void;
  }) => {
    const flyersTotal = flyers.length;
    const hasMore = flyersTotal < total;

    return (
      <InfiniteScroll
        style={{ overflow: 'hidden' }}
        dataLength={flyersTotal}
        next={nextPage}
        hasMore={hasMore}
        scrollThreshold={0.6}
        loader={<LoadingSpinner />}
      >
        <Masonry
          breakpointCols={MasonryBreakpointCols}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {flyers.map(
            ({
              flyerId,
              frontImageUrl,
              isFavorite,
              title,
              status,
              email,
              pages,
              type,
              isExpandedCommunity,
              titleTranslations,
              isCommunityFree,
            }) => (
              <FlyerCard
                key={flyerId}
                id={flyerId}
                title={title}
                thumbnail={frontImageUrl}
                isFavorite={isFavorite}
                inactive={status === FlyerStatus.expired}
                status={status}
                flyerType={type}
                audienceId={audienceId}
                trackId={trackId}
                trackingEventFrom={trackingEventDetail}
                isExpandedCommunity={isExpandedCommunity}
                titleTranslations={titleTranslations}
                showTag
                isCommunityFree={isCommunityFree}
              />
            )
          )}
        </Masonry>
      </InfiniteScroll>
    );
  };

  const activeFlyersList = useMemo(
    () =>
      activeFlyers.length > 0 && (
        <div>
          <div className={styles.currentFlyerHeader}>
            <BodyLargeEmphasized color={colors.slate}>Current Flyers</BodyLargeEmphasized>
            <AccessibleDivButton onClick={() => setIsDrawerOpen(!isDrawerOpen)} ref={anchorRef}>
              <Tag variant="outline" rightIcon={<SwapVertIcon sx={{ fontSize: '15px' }} />}>
                Sort
              </Tag>
            </AccessibleDivButton>
          </div>
          {renderInfiniteScroll({
            flyers: activeFlyers,
            total: activeFlyersTotal,
            nextPage: fetchMoreActiveFlyers,
          })}
        </div>
      ),
    [activeFlyers, activeFlyersTotal, isDrawerOpen]
  );

  const expiredFlyersList = useMemo(
    () =>
      expiredFlyers.length > 0 && (
        <div className={styles.section}>
          <div className={styles.dividerPadding}>
            <Divider />
          </div>
          <div className={styles.headerContainer}>
            <BodyLargeEmphasized color={colors.slate}>Expired Flyers</BodyLargeEmphasized>
          </div>
          {renderInfiniteScroll({
            flyers: expiredFlyers,
            total: expiredFlyersTotal,
            nextPage: fetchMoreExpiredFlyers,
          })}
        </div>
      ),
    [expiredFlyers, expiredFlyersTotal]
  );

  const resetFilters = () => {
    clearActiveFilters();
  };

  const EmptyStates = useCallback(() => {
    if (loading) {
      return null;
    }
    if (activeFlyers.length === 0 && isFiltering) {
      return (
        <EmptyState
          title="Currently no flyers that match your filter."
          description="Reset your filter to view other flyers."
          bottom={
            <div className={styles.link} onClick={() => resetFilters()}>
              Reset Filters
            </div>
          }
          illustration={turtleIllustration}
        />
      );
    }
    if (activeFlyers.length === 0 && searchQuery.length > 0) {
      if (expiredFlyers.length > 0) {
        return <NoActiveFlyersFound searchTerm={searchQuery} />;
      }
      return <NoSearchResultsFoundEmptyState searchTerm={searchQuery} />;
    }

    return null;
  }, [loading, activeFlyers, expiredFlyers, searchQuery]);

  return (
    <div ref={ref}>
      <div className={styles.container}>
        <HeaderFixed>
          <div className={styles.searchContainer}>
            <SearchBox
              placeholder="Search..."
              onChange={setSearchQuery}
              isTextBold
              value={searchInputValue}
              onKeyDown={onSearchInputKeyDown}
              showCancelButton
            />
            <ButtonFlatSmall
              onClick={() => navigatePreviousPage()}
              className={styles.buttonCancelSpacing}
              data-testid="cancelSearch"
            >
              Cancel
            </ButtonFlatSmall>
          </div>
        </HeaderFixed>
        <main className={styles.content}>
          <div className={styles.section}>
            {loading && activeFlyers.length === 0 && expiredFlyers.length === 0 && (
              <LoadingSpinner />
            )}
            <EmptyStates />
            {activeFlyersList}
          </div>
          {expiredFlyersList}
        </main>
        <SortMenu
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          onChange={handleSortChange}
          selectedOption={selectedSortValue}
          anchorRef={anchorRef}
          options={sortOptions}
        />
      </div>
    </div>
  );
};
